import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, CheckCircle2, FileDown, Send, Boxes, Trash2, HelpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EUR, StatusPill } from "@/pages/Quotes";

const EST = [["crew_size", "Persone"], ["hours", "Ore"], ["vans", "Furgoni"], ["days", "Giorni"], ["km", "Km"], ["boxes", "Scatoloni"], ["disassembly", "Pezzi smontaggio"]];
const BASIS_QTY = {
  crew_hours: (e) => e.crew_size * e.hours, van_days: (e) => e.vans * e.days, km: (e) => e.km,
  boxes: (e) => e.boxes, disassembly: (e) => e.disassembly,
};

function totals(q) {
  const sub = q.lines.reduce((s, l) => s + (l.enabled ? (Number(l.qty) || 0) * (Number(l.unit_price) || 0) : 0), 0);
  const vat = (sub * (Number(q.vat_rate) || 0)) / 100;
  return { sub, vat, total: sub + vat };
}

async function fetchPdf(q) {
  const r = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
  const name = `Preventivo_${q.number}_${(q.client_name || "cliente").replace(/[^\w]+/g, "_")}.pdf`;
  return new File([r.data], name, { type: "application/pdf" });
}

function download(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url; a.download = file.name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function shareWhatsApp(q, total) {
  const file = await fetchPdf(q);
  const text = `Buongiorno ${q.client_name}, le inviamo il preventivo ${q.number} per il trasloco. Totale: ${EUR.format(total)} IVA inclusa. Restiamo a disposizione! — In & Out Traslochi`;
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], text }); } catch (err) { if (err?.name !== "AbortError") throw err; }
    return;
  }
  download(file);
  const phone = (q.client_phone || "").replace(/[^\d]/g, "");
  window.open(`https://wa.me/${phone.length === 10 ? "39" + phone : phone}?text=${encodeURIComponent(text)}`, "_blank");
  toast.success("PDF scaricato: allegalo nella chat WhatsApp del cliente");
}

function Field({ label, k, q, set, type = "text" }) {
  return (
    <label className="text-xs font-semibold text-slate-600">{label}
      <Input type={type} value={q[k] || ""} onChange={(e) => set({ [k]: e.target.value })} className="mt-1" data-testid={`quote-${k.replace(/_/g, "-")}-input`} />
    </label>
  );
}

function Estimates({ q, set }) {
  const change = (k, v) => {
    const est = { ...q.estimates, [k]: Number(v) || 0 };
    set({ estimates: est, lines: q.lines.map((l) => (BASIS_QTY[l.basis] ? { ...l, qty: Math.round(BASIS_QTY[l.basis](est) * 100) / 100 } : l)) });
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4">
      <h3 className="font-display font-bold text-slate-900 mb-1">Stime del lavoro</h3>
      <p className="text-xs text-slate-500 mb-3">Se le cambi, le quantità delle voci collegate si ricalcolano.</p>
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
        {EST.map(([k, l]) => (
          <label key={k} className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{l}
            <Input type="number" min="0" step="0.5" value={q.estimates?.[k] ?? 0} onChange={(e) => change(k, e.target.value)} className="mt-1" data-testid={`quote-est-${k}`} />
          </label>
        ))}
      </div>
    </section>
  );
}

function Lines({ q, set }) {
  const upd = (i, patch) => set({ lines: q.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  return (
    <section className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="quote-lines">
      <h3 className="font-display font-bold text-slate-900 p-4">Costi</h3>
      {q.lines.map((l, i) => (
        <div key={l.key} className={`grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1.5fr_1fr_1fr_1fr] gap-2 items-center p-3 ${l.enabled ? "" : "opacity-50"}`} data-testid={`quote-line-${l.key}`}>
          <Switch checked={l.enabled} onCheckedChange={(c) => upd(i, { enabled: c })} data-testid={`quote-line-enabled-${l.key}`} />
          <div className="font-semibold text-sm text-slate-900">{l.label}<div className="text-[11px] font-normal text-slate-500">{l.unit}</div></div>
          <label className="col-start-2 sm:col-start-auto text-[10px] font-bold uppercase tracking-wider text-slate-400"><span className="sm:hidden">Quantità</span>
            <Input type="number" min="0" step="0.5" value={l.qty} onChange={(e) => upd(i, { qty: e.target.value })} aria-label="Quantità" data-testid={`quote-line-qty-${l.key}`} />
          </label>
          <label className="col-start-2 sm:col-start-auto text-[10px] font-bold uppercase tracking-wider text-slate-400"><span className="sm:hidden">Prezzo unitario €</span>
            <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => upd(i, { unit_price: e.target.value })} aria-label="Prezzo unitario" data-testid={`quote-line-price-${l.key}`} />
          </label>
          <div className="col-start-2 sm:col-start-auto text-right font-semibold text-slate-900" data-testid={`quote-line-total-${l.key}`}>{EUR.format((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</div>
        </div>
      ))}
    </section>
  );
}

function TotalsBox({ q, set, t }) {
  return (
    <section className="bg-slate-900 text-white rounded-2xl p-5 space-y-2" data-testid="quote-totals">
      <div className="flex justify-between text-sm text-slate-300"><span>Imponibile</span><span data-testid="quote-subtotal">{EUR.format(t.sub)}</span></div>
      <div className="flex justify-between items-center text-sm text-slate-300">
        <span className="flex items-center gap-2">IVA <Input type="number" min="0" max="50" value={q.vat_rate} onChange={(e) => set({ vat_rate: e.target.value })} className="w-16 h-8 bg-slate-800 border-slate-700 text-white" data-testid="quote-vat-input" />%</span>
        <span data-testid="quote-vat">{EUR.format(t.vat)}</span>
      </div>
      <div className="flex justify-between font-display text-2xl font-extrabold pt-2 border-t border-slate-700"><span>Totale</span><span data-testid="quote-total">{EUR.format(t.total)}</span></div>
      {q.ai_suggested_total > 0 && <p className="text-xs text-slate-400 flex gap-1.5"><Sparkles className="w-3.5 h-3.5 shrink-0 text-violet-300" /> ChatGPT, dallo storico dei lavori, avrebbe stimato circa {EUR.format(q.ai_suggested_total)}. {q.price_reasoning}</p>}
    </section>
  );
}

function Actions({ q, dirty, busy, save, approve, toJob, remove, t }) {
  const run = (fn) => async () => { try { await fn(); } catch (e) { toast.error(formatApiError(e)); } };
  const approved = (q.status === "approvato" && !dirty) || !!q.job_id;
  return (
    <div className="flex flex-wrap gap-2 justify-end sticky bottom-3 bg-white/90 backdrop-blur-md border border-slate-200 rounded-2xl p-3 shadow-sm">
      <Button variant="ghost" onClick={remove} className="mr-auto text-rose-600" data-testid="quote-delete-button"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>
      {!approved && <Button variant="outline" onClick={save} disabled={busy || !dirty} data-testid="quote-save-button"><Save className="w-4 h-4 mr-2" /> Salva bozza</Button>}
      {!approved && <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quote-approve-button"><CheckCircle2 className="w-4 h-4 mr-2" /> Ho controllato: approva</Button>}
      {approved && <Button variant="outline" onClick={run(async () => download(await fetchPdf(q)))} data-testid="quote-pdf-button"><FileDown className="w-4 h-4 mr-2" /> Scarica PDF</Button>}
      {approved && <Button onClick={run(() => shareWhatsApp(q, t.total))} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="quote-whatsapp-button"><Send className="w-4 h-4 mr-2" /> Invia su WhatsApp</Button>}
      {approved && !q.job_id && <Button onClick={toJob} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="quote-to-job-button"><Boxes className="w-4 h-4 mr-2" /> Crea lavoro</Button>}
      {q.job_id && <Button onClick={() => window.location.assign(`/lavori/${q.job_id}`)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="quote-open-job-button"><Boxes className="w-4 h-4 mr-2" /> Apri lavoro</Button>}
    </div>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch((e) => toast.error(formatApiError(e))); }, [id]);
  const t = useMemo(() => (q ? totals(q) : null), [q]);
  if (!q) return null;
  const set = (patch) => {
    if (q.job_id) { toast.error("Lavoro già creato da questo preventivo: modifica direttamente il lavoro"); return; }
    setQ({ ...q, ...patch }); setDirty(true);
  };
  const body = () => ({ ...q, vat_rate: Number(q.vat_rate) || 0, lines: q.lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })) });
  const call = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const save = () => call(async () => { const { data } = await api.put(`/quotes/${id}`, body()); setQ(data); setDirty(false); toast.success("Bozza salvata"); });
  const approve = () => call(async () => {
    if (dirty) await api.put(`/quotes/${id}`, body());
    const { data } = await api.post(`/quotes/${id}/approve`);
    setQ(data); setDirty(false); toast.success("Preventivo approvato: ora puoi inviarlo");
  });
  const toJob = () => call(async () => { const { data } = await api.post(`/quotes/${id}/to-job`); toast.success("Lavoro creato"); nav(`/lavori/${data.job_id}`); });
  const remove = () => { if (window.confirm("Eliminare questo preventivo?")) call(async () => { await api.delete(`/quotes/${id}`); nav("/preventivi"); }); };

  return (
    <div className="space-y-5 pb-4" data-testid="quote-detail-page">
      <button onClick={() => nav("/preventivi")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="quote-back-button"><ArrowLeft className="w-4 h-4" /> Preventivi</button>
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">{q.number}</h1>
        <StatusPill status={dirty ? "bozza" : q.status} />
        {q.approved_by && !dirty && q.status === "approvato" && <span className="text-xs text-slate-500">approvato da {q.approved_by}</span>}
      </header>
      {q.status === "approvato" && dirty && <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-xl p-3" data-testid="quote-reapprove-warning">Hai modificato un preventivo approvato: torna in bozza e va riapprovato.</div>}
      {q.missing_info?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="quote-missing-info">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900 mb-1"><HelpCircle className="w-3.5 h-3.5" /> Da chiedere al cliente</div>
          <ul className="list-disc pl-5 text-xs text-amber-900 space-y-0.5">{q.missing_info.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}
      <section className="bg-white border border-slate-200 rounded-2xl p-4 grid sm:grid-cols-2 gap-3">
        <Field label="Cliente" k="client_name" q={q} set={set} />
        <Field label="Telefono" k="client_phone" q={q} set={set} />
        <Field label="Partenza" k="address_from" q={q} set={set} />
        <Field label="Arrivo" k="address_to" q={q} set={set} />
        <Field label="Data" k="scheduled_date" q={q} set={set} type="date" />
        <Field label="Orario" k="start_time" q={q} set={set} type="time" />
        <label className="sm:col-span-2 text-xs font-semibold text-slate-600">Note per il cliente (vanno nel PDF)
          <Textarea rows={3} value={q.notes || ""} onChange={(e) => set({ notes: e.target.value })} className="mt-1" data-testid="quote-notes-input" />
        </label>
        {q.items?.length > 0 && <p className="sm:col-span-2 text-xs text-slate-500" data-testid="quote-items-summary"><b>Oggetti ({q.items.length}):</b> {q.items.map((i) => `${i.name}${i.quantity > 1 ? " ×" + i.quantity : ""}`).join(", ")}</p>}
      </section>
      <Estimates q={q} set={set} />
      <Lines q={q} set={set} />
      <TotalsBox q={q} set={set} t={t} />
      <Actions q={q} dirty={dirty} busy={busy} save={save} approve={approve} toJob={toJob} remove={remove} t={t} />
    </div>
  );
}
